/**
 * Post-claim redirect allowlist (LEV-2451).
 *
 * The claim page's `gate` search param names the signup-gate action that
 * sent the visitor to sign up (`SignupPromptModal` builds the sign-up
 * redirect as `/claim/{projectId}?gate=<action>`, see
 * `src/components/molecules/modals/SignupPromptModal.tsx`). A destination
 * here is built ONLY from the just-claimed `projectId` and a constant path,
 * never from the query string itself, so a crafted `gate` value can at most
 * select "no match" (falls through to `/${projectId}`) — it can never point
 * off-site or at a different project. Cross-compatible: imported from the
 * client-only claim page, but holds no client-only API.
 */
import { programsFirstStopHref } from "@/constants/acceleratorsRow";
import type { AppFormatId } from "@/constants/accelerators/applications";
import {
  PROGRAMS,
  PROGRAM_SLUGS,
  buildProgramApplicationHref,
  type ProgramSlug,
} from "@/constants/programs";
import { isPremierProgramFormat, rowProgramIdForSlug } from "@/lib/accelerators/firstStop";
import { buildAuthRedirectHref } from "@/lib/referral";
import { PROJECT_ID_PATTERN } from "@/lib/routes";

type ProgramClaimGate = `program.${ProgramSlug}`;

/** Every gate action this allowlist recognizes. Keep in sync with the keys below. */
type ClaimRedirectGate = "yc.start" | ProgramClaimGate;

// LEV-2722: a program lander's resume card opens that program's application
// once the claim lands, by the same URL the lander's own hand-off opens
// (entry=program_handoff included), so PhoneRouteGate treats a phone arrival
// alike whichever way the founder came back.
const PROGRAM_CLAIM_REDIRECTS = Object.fromEntries(
  PROGRAM_SLUGS.map((slug) => [
    `program.${slug}`,
    (projectId: string) => buildProgramApplicationHref(projectId, slug),
  ]),
) as Record<ProgramClaimGate, (projectId: string) => string>;

const CLAIM_REDIRECTS: Readonly<
  Record<ClaimRedirectGate, (projectId: string) => string>
> = {
  ...PROGRAM_CLAIM_REDIRECTS,
  // Matches the canvas Start handlers' own URL (useYcCanvas.ts), with
  // `yc_entry=signup_gate` so the panel's auto-draft attributes the eventual
  // yc_app_started to the signup gate rather than re-using canvas_banner/block
  // (those fire their own yc_app_started before the gate gets a chance to run).
  "yc.start": (projectId) =>
    `/${projectId}/labs/accelerator-apps?format=yc&yc_entry=signup_gate`,
};

/**
 * The path to replace to after a successful claim. `gate` is the claim page's
 * raw `?gate=` search param (untrusted, but only ever used as a lookup key,
 * never interpolated into the result). Unknown or missing gate values resolve
 * to the project root, matching pre-LEV-2451 behavior for every other gate.
 */
export function resolveClaimRedirectPath(
  projectId: string,
  gate: string | null | undefined,
): string {
  const build = gate && isClaimRedirectGate(gate) ? CLAIM_REDIRECTS[gate] : null;
  return build ? build(projectId) : `/${projectId}`;
}

/**
 * The program a claim gate opens the application of, or null for any other
 * gate. The claim page reads it to check the format is launched and, on a
 * phone, to start the draft before it follows resolveClaimRedirectPath.
 */
export function programSlugFromClaimGate(gate: string | null | undefined): ProgramSlug | null {
  if (!gate || !isClaimRedirectGate(gate) || !gate.startsWith("program.")) return null;
  return PROGRAM_SLUGS.find((slug) => gate === `program.${slug}`) ?? null;
}

/** The new owner's flags a program claim's destination depends on, all answered. */
export interface ProgramClaimArrivalFlags {
  straightIn: boolean;
  rowOn: boolean;
  launchedFormatIds: readonly AppFormatId[];
  phoneHandoffActive: boolean;
}

/**
 * Where a claim through a program gate sends the new owner, and whether the
 * draft must be started first. MUST decide in the same order as the lander's
 * hand-off route (src/app/[projectId]/apply/[program]/page.tsx), which this
 * mirrors for a project whose Big Idea already exists:
 * - straight-in OFF or the format unlaunched: the project root.
 * - accelerators-row ON for a program in the row: a premier program's first
 *   stop (the row's own `?accelerator=` link cannot select a program with no
 *   application row yet), any other program's canvas. No draft: the
 *   founder confirms the program there, as after the hand-off route.
 * - otherwise the application, with the draft started first on a phone
 *   (ProgramHandoffScreen): PhoneRouteGate keeps the page from drafting.
 */
export function programClaimArrival(
  projectId: string,
  slug: ProgramSlug,
  flags: ProgramClaimArrivalFlags,
): { href: string; startDraft: boolean } {
  const projectHref = `/${projectId}`;
  const formatId = PROGRAMS[slug].appFormatId;
  if (!flags.straightIn || !flags.launchedFormatIds.includes(formatId)) {
    return { href: projectHref, startDraft: false };
  }
  const rowProgramId = rowProgramIdForSlug(slug);
  if (rowProgramId && flags.rowOn) {
    return {
      href: isPremierProgramFormat(formatId)
        ? programsFirstStopHref(projectId, rowProgramId)
        : projectHref,
      startDraft: false,
    };
  }
  return {
    href: resolveClaimRedirectPath(projectId, `program.${slug}`),
    startDraft: flags.phoneHandoffActive,
  };
}

// Own keys only: `in` would also accept inherited names (`__proto__`, `toString`).
function isClaimRedirectGate(value: string): value is ClaimRedirectGate {
  return Object.hasOwn(CLAIM_REDIRECTS, value);
}

/**
 * Where a program lander's resume card sends a signed-out visitor holding
 * a live anonymous project (LEV-2722): sign-up, then the claim page, which
 * opens the program's application. The application routes admit only a
 * signed-in owner, and an anonymous project has none until it is claimed.
 * Sign-up, not sign-in, as SignupPromptModal sends anonymous visitors.
 *
 * INVARIANT: the result is always a same-origin relative path, built only
 * from a pattern-checked project id and constants. An id that fails the
 * pattern gets "/" rather than a path built around it.
 */
export function anonProgramResumeHref(
  projectId: string,
  slug: ProgramSlug,
): string {
  if (!PROJECT_ID_PATTERN.test(projectId)) return "/";
  const gate: ProgramClaimGate = `program.${slug}`;
  return buildAuthRedirectHref("/sign-up", {
    redirectUrl: `/claim/${projectId}?gate=${gate}`,
  });
}
